<template>
  <div class="appdeploy-onboarding-page">

    <!-- Main Container -->
    <div class="onboarding-container">
      <!-- Page Header -->
      <div class="page-header">
        <h1 class="page-title">App Deploy Client Onboarding</h1>
        <p class="page-subtitle">
          Streamlined client onboarding via Azure AD admin consent. Authorize
          the app to operate within client environments with required
          permissions.
        </p>
      </div>

      <!-- Client Information Card -->
      <div class="client-info-section">
        <div class="section-header">
          <h3>
            <span class="section-icon">🔐</span>
            Required Client Information
          </h3>
        </div>

        <div class="form-grid">
          <!-- Client Code Field -->
          <div class="form-group">
            <label for="client-code">Client Code</label>
            <input
              type="text"
              id="client-code"
              v-model="clientCode"
              @input="validateClientCode"
              :class="[
                'form-input',
                {
                  'is-invalid': clientCodeError,
                  'is-valid': clientCodeSuccess && !clientCodeError,
                },
              ]"
              required
              placeholder="e.g., ABC"
            />
            <div class="validation-messages">
              <span v-if="clientCodeError" class="error-message">
                <span class="error-icon">⚠️</span>
                {{ clientCodeError }}
              </span>
              <span
                v-if="clientCodeSuccess && !clientCodeError"
                class="success-message"
              >
                <span class="success-icon">✓</span>
                {{ clientCodeSuccess }}
              </span>
            </div>
          </div>

          <!-- Domain Field -->
          <div class="form-group">
            <label for="domain">Domain</label>
            <input
              type="text"
              id="domain"
              v-model="domain"
              class="form-input"
              required
              placeholder="e.g., example.com"
            />
          </div>

          <!-- Tenant ID Field -->
          <div class="form-group full-width">
            <label for="tenant-id">Tenant ID</label>
            <input
              type="text"
              id="tenant-id"
              v-model="clientTenantId"
              class="form-input"
              readonly
              placeholder="Will be populated after authentication"
            />
            <div class="field-hint">
              Automatically populated after global admin authentication
            </div>
          </div>
        </div>

        <!-- Authentication Button -->
        <div class="action-section">
          <button
            @click="startAdminConsentFlow"
            class="btn btn-primary"
            :disabled="!isClientInfoValid || isAuthenticating"
          >
            <span v-if="isAuthenticating" class="loading-spinner">⏳</span>
            <span v-else>🔑</span>
            {{
              isAuthenticating ? "Authenticating..." : "Login as Global Admin"
            }}
          </button>
        </div>

        <!-- Authentication Status -->
        <transition name="fade">
          <div
            v-if="clientTenantId && globalAdminToken && enterpriseAppId"
            class="status-card success-card"
          >
            <div class="status-header">
              <span class="status-icon">✅</span>
              <h4>Authentication Successful</h4>
            </div>
            <div class="status-details">
              <div class="detail-item">
                <span class="detail-label">Tenant ID:</span>
                <code class="detail-value">{{ clientTenantId }}</code>
              </div>
              <div class="detail-item">
                <span class="detail-label">Enterprise App ID:</span>
                <code class="detail-value">{{ enterpriseAppId }}</code>
              </div>
              <div class="detail-item">
                <span class="detail-label">Status:</span>
                <span class="status-badge status-active">Active</span>
              </div>
            </div>
          </div>
        </transition>

        <!-- Complete Onboarding Section -->
        <transition name="fade">
          <div
            v-if="globalAdminToken && enterpriseAppId"
            class="completion-section"
          >
            <div class="section-divider"></div>

            <div class="section-header">
              <h3>
                <span class="section-icon">🚀</span>
                Complete Onboarding
              </h3>
            </div>

            <p class="completion-description">
              Client authentication successful. Click below to complete the App
              Deploy onboarding process.
            </p>

            <button
              @click="completeOnboarding"
              class="btn btn-success"
              :disabled="isCompleting"
            >
              <span v-if="isCompleting" class="loading-spinner">⏳</span>
              <span v-else>🎯</span>
              {{
                isCompleting
                  ? "Completing Onboarding..."
                  : "Complete App Deploy Onboarding"
              }}
            </button>

            <!-- Onboarding Status Messages -->
            <transition name="fade">
              <div
                v-if="onboardingStatus.text"
                class="status-message"
                :class="onboardingStatus.type"
              >
                <span class="status-icon">{{
                  getStatusIcon(onboardingStatus.type)
                }}</span>
                {{ onboardingStatus.text }}
              </div>
            </transition>
          </div>
        </transition>
      </div>

      <!-- General Status Messages -->
      <transition name="fade">
        <div v-if="message.text" class="status-message" :class="message.type">
          <span class="status-icon">{{ getStatusIcon(message.type) }}</span>
          {{ message.text }}
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
// Import the necessary components and API functions
import { mapState } from "vuex";
import { msalInstance } from "@/auth.js";
import { msalOnboardInstance } from "@/msalOnboardConfig";
import {
  triggerAppDeployBuild,
  listAzureTables,
  fetchPipelinesbyName,
  getPipelineStatus,
} from "@/azure-devops-api.js";

export default {
  components: {
  },
  data() {
    return {
      // Client information fields
      clientCode: "",
      domain: "",
      clientTenantId: null,

      // Validation
      clientCodeError: "",
      clientCodeSuccess: "",
      clientCodeValidated: false,
      pipelineExists: false,

      // Authentication & App Info
      globalAdminToken: null,
      enterpriseAppId: null,

      // Final onboarding status
      onboardingStatus: { text: "", type: "" },

      // General messages
      message: { text: "", type: "" },
    };
  },
  computed: {
    ...mapState(["isLoggedIn"]),

    // Validation for the client info fields
    isClientInfoValid() {
      return (
        this.clientCodeValidated &&
        this.clientCode.trim() !== "" &&
        this.domain.trim() !== "" &&
        !this.pipelineExists
      );
    },
  },
  methods: {
    // Validate client code format and check if it exists in Azure Storage tables
    async validateClientCode() {
      const clientCodePattern = /^[A-Z]{3}$/;
      if (!clientCodePattern.test(this.clientCode)) {
        this.clientCodeError =
          "Client code must be exactly 3 uppercase letters.";
        this.clientCodeSuccess = "";
        this.clientCodeValidated = false;
      } else {
        this.clientCodeError = "";
        await this.checkClientCodeInTables();
        if (!this.clientCodeError) {
          await this.checkForExistingPipeline();
        }
      }
    },

    // Check if client code exists in Azure Storage tables
    async checkClientCodeInTables() {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length) {
          const accessTokenRequest = {
            account: accounts[0],
            scopes: ["https://storage.azure.com/.default"],
          };
          const response = await msalInstance.acquireTokenSilent(
            accessTokenRequest
          );
          const accessToken = response.accessToken;

          const tables = await listAzureTables(accessToken);
          const tableExists = tables.some((table) =>
            table.name.startsWith(this.clientCode)
          );

          if (tableExists) {
            this.clientCodeSuccess = `Table found for client code ${this.clientCode}.`;
            this.clientCodeError = "";
            this.clientCodeValidated = true;
          } else {
            this.clientCodeError = `No tables found starting with ${this.clientCode}. Please create Client App Table first!`;
            this.clientCodeSuccess = "";
            this.clientCodeValidated = false;
          }
        } else {
          throw new Error("No accounts available for authentication.");
        }
      } catch (error) {
        console.error("Error checking client code in tables:", error);
        this.clientCodeError = "Error checking client code in tables.";
        this.clientCodeSuccess = "";
        this.clientCodeValidated = false;
      }
    },

    // Check if an Azure DevOps pipeline already exists for the client
    async checkForExistingPipeline() {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length) {
          const accessTokenRequest = {
            account: accounts[0],
            scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
          };
          const response = await msalInstance.acquireTokenSilent(
            accessTokenRequest
          );
          const accessToken = response.accessToken;

          const pipelines = await fetchPipelinesbyName(accessToken, "Tenants"); // Replace with your actual project name
          const pipelineExists = pipelines.some((pipeline) =>
            pipeline.folder.includes(this.clientCode)
          );

          if (pipelineExists) {
            this.clientCodeError = `${this.clientCode} has already been onboarded.`;
            this.clientCodeValidated = false;
            this.pipelineExists = true;
          } else {
            this.pipelineExists = false;
          }
        } else {
          throw new Error("No accounts available for authentication.");
        }
      } catch (error) {
        console.error("Error checking pipelines:", error);
        this.clientCodeError = "Error checking pipelines.";
        this.clientCodeValidated = false;
        this.pipelineExists = true;
      }
    },
    // Method to start admin consent flow
    startAdminConsentFlow() {
      if (!this.isClientInfoValid) {
        if (this.pipelineExists) {
          alert(
            "This client has already been onboarded. Please check the existing pipeline."
          );
        } else {
          alert(
            "Please complete all required fields with valid information before proceeding."
          );
        }
        return;
      }

      // Store the client information in sessionStorage for retrieval after redirect
      sessionStorage.setItem("appDeployClientCode", this.clientCode);
      sessionStorage.setItem("appDeployDomain", this.domain);

      // Use your actual App Deploy application client ID
      const clientId = "14619720-47d8-4754-95b1-6ce1973e71b5";
      const redirectUri = `${window.location.origin}/AppDeploy-Onboarding-New`;

      const adminConsentUrl = `https://login.microsoftonline.com/common/adminconsent?client_id=${clientId}&redirect_uri=${redirectUri}`;

      // Redirect the user to the Microsoft admin consent page
      window.location.href = adminConsentUrl;
    },

    // Method to prompt login for global admin after consent
    async promptGlobalAdminLogin() {
      try {
        console.log("Starting global admin login...");

        const loginResponse = await msalOnboardInstance.loginPopup({
          scopes: [
            "User.Read",
            "Application.Read.All",
            "Application.ReadWrite.All",
            "RoleManagement.ReadWrite.Directory",
            "Directory.Read.All",
            "https://management.azure.com/user_impersonation",
          ],
        });

        console.log(
          "Login successful, received token:",
          loginResponse.accessToken ? "Yes" : "No"
        );
        this.globalAdminToken = loginResponse.accessToken;

        // Show user that we're searching for the enterprise app
        this.message = {
          text: "Authenticated successfully. Searching for enterprise app...",
          type: "info",
        };

        // Now get the enterprise app ID from the client tenant with retry logic
        await this.getEnterpriseAppId();
      } catch (err) {
        console.error("Login failed after admin consent:", err);

        let errorMessage = "Failed to authenticate after admin consent.";

        if (err.errorCode) {
          errorMessage += ` Error code: ${err.errorCode}`;
        }

        if (err.errorMessage) {
          errorMessage += ` Details: ${err.errorMessage}`;
        } else if (err.message) {
          errorMessage += ` Details: ${err.message}`;
        }

        errorMessage += " Please try again.";

        this.message = {
          text: errorMessage,
          type: "error",
        };
      }
    },

    async getEnterpriseAppId() {
      const appDeployClientId = "14619720-47d8-4754-95b1-6ce1973e71b5";
      const maxRetries = 6; // Increased to 6 attempts
      const baseDelay = 3000; // Start with 3 seconds

      // Show user that we're searching for the enterprise app
      this.message = {
        text: "Searching for enterprise application in tenant... This may take a few moments.",
        type: "info",
      };

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`Attempt ${attempt} to find enterprise app...`);

          // Update user on retry attempts
          if (attempt > 1) {
            this.message = {
              text: `Enterprise app not found yet, retrying... (Attempt ${attempt}/${maxRetries})`,
              type: "info",
            };
          }

          const response = await fetch(
            `https://graph.microsoft.com/v1.0/servicePrincipals?$filter=appId eq '${appDeployClientId}'`,
            {
              headers: {
                Authorization: `Bearer ${this.globalAdminToken}`,
                "Content-Type": "application/json",
              },
            }
          );

          console.log(
            `Attempt ${attempt} - Graph API response status:`,
            response.status
          );

          if (response.ok) {
            const data = await response.json();
            console.log(`Attempt ${attempt} - Graph API response data:`, data);

            if (data.value && data.value.length > 0) {
              // Success! Found the enterprise app
              this.enterpriseAppId = data.value[0].appId;
              console.log(
                "Enterprise App Application ID found:",
                this.enterpriseAppId
              );

              // Clear the searching message
              this.message = { text: "", type: "" };
              return; // Exit the retry loop
            } else {
              // Enterprise app not found yet
              console.log(
                `Attempt ${attempt} - No enterprise app found in response`
              );

              if (attempt === maxRetries) {
                throw new Error(
                  "Enterprise app not found in target tenant after maximum retries. The admin consent may not have completed properly, or there may be a propagation delay. Please wait a few minutes and refresh the page to try again."
                );
              }

              // Wait before next retry with exponential backoff
              const delay = baseDelay * Math.pow(1.5, attempt - 1);
              console.log(
                `Enterprise app not found, waiting ${delay}ms before retry...`
              );
              await this.sleep(delay);
            }
          } else {
            // Handle HTTP errors
            const errorText = await response.text();
            console.error(
              `Attempt ${attempt} - Graph API error response:`,
              errorText
            );

            if (response.status === 401) {
              throw new Error(
                "Authentication failed. Please try the admin consent flow again."
              );
            }

            throw new Error(
              `Graph API error: ${response.status} - ${errorText}`
            );
          }
        } catch (error) {
          console.error(`Attempt ${attempt} failed:`, error);

          // If this was the last attempt, show the error
          if (attempt === maxRetries) {
            console.error(
              "Error getting enterprise app ID after all retries:",
              error
            );
            this.message = {
              text: `Failed to retrieve enterprise app information: ${error.message}`,
              type: "error",
            };
            return;
          }

          // For authentication errors, don't retry
          if (error.message.includes("Authentication failed")) {
            this.message = {
              text: error.message,
              type: "error",
            };
            return;
          }

          // Wait before next retry for other errors too
          const delay = baseDelay * Math.pow(1.5, attempt - 1);
          console.log(`Error occurred, waiting ${delay}ms before retry...`);
          await this.sleep(delay);
        }
      }
    },

    // Method to complete the onboarding process
    async completeOnboarding() {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length) {
          const accessTokenRequest = {
            account: accounts[0],
            scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
          };
          const response = await msalInstance.acquireTokenSilent(
            accessTokenRequest
          );
          const accessToken = response.accessToken;

          this.onboardingStatus = {
            text: "Triggering App Deploy onboarding pipeline...",
            type: "info",
          };

          const projectId = "65a332c6-445e-4c72-a097-db3513d145d5"; // App Deploy project ID
          const pipelineBuildId = "191"; // App Deploy pipeline build ID

          const buildResponse = await triggerAppDeployBuild(
            accessToken,
            projectId,
            pipelineBuildId,
            this.clientCode,
            this.domain,
            this.clientTenantId,
            this.enterpriseAppId
          );

          if (buildResponse && buildResponse.state === "inProgress") {
            this.onboardingStatus = {
              text: "Pipeline triggered successfully! Processing your request...",
              type: "success",
            };

            // Start polling the pipeline status
            this.pollPipelineStatus(
              accessToken,
              projectId,
              pipelineBuildId,
              buildResponse.id
            );
          } else {
            this.onboardingStatus = {
              text: "Failed to trigger the onboarding pipeline.",
              type: "error",
            };
          }
        } else {
          throw new Error("No accounts available for authentication.");
        }
      } catch (error) {
        console.error("Error during onboarding completion:", error);
        this.onboardingStatus = {
          text: "Error: " + error.message,
          type: "error",
        };
      }
    },

    // Poll pipeline status
    async pollPipelineStatus(accessToken, projectId, pipelineId, runId) {
      const pollingInterval = 5000; // Poll every 5 seconds
      const successMessageDuration = 3000; // Show success message for 3 seconds before updating

      const updateStatus = (text, type) => {
        this.onboardingStatus = { text, type };
      };

      // Preserve the success message for a set duration
      setTimeout(() => {
        updateStatus("", ""); // Clear the success message
        checkStatus(); // Start polling after success message duration
      }, successMessageDuration);

      const checkStatus = async () => {
        try {
          console.log("Checking pipeline status...");
          const status = await getPipelineStatus(
            accessToken,
            projectId,
            pipelineId,
            runId
          );
          console.log("Pipeline status response: ", status);
          updateStatus(
            `Pipeline status: ${status.state} - ${
              status.result || "In Progress"
            }`,
            status.result === "succeeded" ? "success" : "info"
          );

          // Stop polling if the pipeline is no longer running
          if (status.state !== "inProgress") {
            return;
          }

          // Continue polling
          setTimeout(checkStatus, pollingInterval);
        } catch (error) {
          console.error("Error checking pipeline status:", error);
          updateStatus(
            "Error checking pipeline status: " + error.message,
            "error"
          );
        }
      };
    },

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
  },

  mounted() {
    // Check for saved form data and restore it
    const savedClientCode = sessionStorage.getItem("appDeployClientCode");
    const savedDomain = sessionStorage.getItem("appDeployDomain");

    if (savedClientCode) {
      this.clientCode = savedClientCode;
      this.validateClientCode();
    }

    if (savedDomain) {
      this.domain = savedDomain;
    }

    // Check for URL parameters (from admin consent redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const tenantId = urlParams.get("tenant");
    const error = urlParams.get("error");
    const errorDescription = urlParams.get("error_description");

    // Handle errors from admin consent flow
    if (error) {
      let errorMessage = "Admin consent failed: ";
      if (error === "access_denied") {
        if (errorDescription && errorDescription.includes("AADSTS1003031")) {
          errorMessage =
            "Application permissions are not properly configured. Please contact your administrator to configure the required API permissions in the Azure AD app registration.";
        } else {
          errorMessage =
            "Admin consent was denied or cancelled. Please try again and accept the required permissions.";
        }
      } else {
        errorMessage += decodeURIComponent(errorDescription || error);
      }

      this.message = {
        text: errorMessage,
        type: "error",
      };

      // Clear any stored session data since consent failed
      sessionStorage.removeItem("appDeployClientCode");
      sessionStorage.removeItem("appDeployDomain");
      sessionStorage.removeItem("appDeployClientTenantId");
      return;
    }

    // Handle successful consent
    if (tenantId) {
      this.clientTenantId = tenantId;
      sessionStorage.setItem("appDeployClientTenantId", tenantId);

      // Now prompt login using MSAL to get global admin token and find enterprise app
      this.promptGlobalAdminLogin();
    } else {
      // Check if we had previously stored a tenant ID
      const savedTenantId = sessionStorage.getItem("appDeployClientTenantId");
      if (savedTenantId) {
        this.clientTenantId = savedTenantId;

        // Check if we need to reauth
        if (!this.globalAdminToken) {
          this.promptGlobalAdminLogin();
        }
      }
    }
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Page Container */
.appdeploy-onboarding-page {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  background-color: #0f172a;
  min-height: 100vh;
  color: #e2e8f0;
}

/* Header */
.header {
  background: #1e293b;
  border-bottom: 1px solid #334155;
  position: sticky;
  top: 0;
  z-index: 100;
}

.top-bar {
  padding: 1rem 0;
}

.container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 2rem;
}

.logo img {
  height: 40px;
  width: auto;
}

/* Main Container */
.onboarding-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

/* Page Header */
.page-header {
  text-align: center;
  margin-bottom: 3rem;
}

.page-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #f1f5f9;
  margin-bottom: 0.75rem;
}

.page-subtitle {
  font-size: 1rem;
  color: #94a3b8;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

/* Client Info Section */
.client-info-section {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 0.75rem;
  padding: 2rem;
  transition: box-shadow 0.3s ease;
}

.client-info-section:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-icon {
  font-size: 1.25rem;
  opacity: 0.8;
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #e2e8f0;
  margin-bottom: 0.5rem;
  display: block;
}

/* Form Inputs */
.form-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  border: 1px solid #334155;
  border-radius: 0.375rem;
  background-color: #0f172a;
  color: #e2e8f0;
  transition: all 0.2s;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background-color: #1e293b;
}

.form-input[readonly] {
  background-color: #0f172a;
  color: #64748b;
  cursor: not-allowed;
  opacity: 0.7;
}

.form-input.is-invalid {
  border-color: #ef4444;
}

.form-input.is-invalid:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.form-input.is-valid {
  border-color: #10b981;
}

.form-input::placeholder {
  color: #475569;
  font-style: italic;
}

/* Field Hints */
.field-hint {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.25rem;
  font-style: italic;
}

/* Validation Messages */
.validation-messages {
  margin-top: 0.25rem;
  min-height: 1.25rem;
}

.error-message,
.success-message {
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.error-message {
  color: #ef4444;
}

.success-message {
  color: #10b981;
}

.error-icon,
.success-icon {
  font-size: 0.875rem;
}

/* Buttons */
.btn {
  padding: 0.625rem 1.25rem;
  border-radius: 0.375rem;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: inherit;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.btn-success {
  background-color: #10b981;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Action Section */
.action-section {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
}

/* Status Cards */
.status-card {
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin: 1.5rem 0;
}

.success-card {
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.status-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.status-header h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #10b981;
  margin: 0;
}

.status-icon {
  font-size: 1.5rem;
}

.status-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.detail-label {
  color: #94a3b8;
  min-width: 120px;
}

.detail-value {
  font-family: "Monaco", "Menlo", monospace;
  background: #0f172a;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  color: #93bbfe;
  font-size: 0.75rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-active {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.2);
}

/* Completion Section */
.completion-section {
  margin-top: 2rem;
}

.section-divider {
  height: 1px;
  background: #334155;
  margin: 2rem 0;
}

.completion-description {
  color: #94a3b8;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  line-height: 1.6;
}

/* Status Messages */
.status-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  margin-top: 1.5rem;
  font-size: 0.875rem;
  border: 1px solid;
}

.status-message.success {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border-color: rgba(16, 185, 129, 0.2);
}

.status-message.error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.2);
}

.status-message.info,
.status-message.status-info {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-color: rgba(59, 130, 246, 0.2);
}

/* Loading Spinner */
.loading-spinner {
  display: inline-block;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #0f172a;
}

::-webkit-scrollbar-thumb {
  background: #475569;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

/* Responsive Design */
@media (max-width: 768px) {
  .onboarding-container {
    padding: 1rem;
  }

  .client-info-section {
    padding: 1.5rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}
</style>
